/**
 * Per-stage wall-clock measurement helper.
 *
 * The parser + analyser pipelines now thread a {@link StageMeasure}
 * through every stage so the timings flow into the provenance rollup
 * (CustodySurface renders this from Wave 4D) and into a rolling
 * session-level p50 / p95 / p99 sampler the DebugOverlay reads.
 *
 * Three surfaces:
 *
 *  1. {@link StageMeasure} — per-pipeline helper. `start(name)` returns
 *  a `record()` closure; `finalise()` returns the per-stage rollup +
 *  total wall-clock. Stages are advisory — overruns log a warning
 *  to the parser-trace channel but never abort the pipeline.
 *  2. {@link PerfSession} — process-singleton sampler that absorbs the
 *  most recent N parse / analyse rollups, exposed to DebugOverlay
 *  for the p50 / p95 / p99 view.
 *  3. {@link PERF_BUDGETS_PER_STAGE} / {@link PERF_STAGE_NAMES} — the
 *  twelve named stages and their per-fixture-size budgets. Set at
 *  ~2× the median measured time (Wave 9A profile run); the test
 *  harness asserts each stage clears its budget × 2 for the
 *  relevant fixture size.
 *
 * All pure JS — no imports from React, the parser, or analysis. Safe
 * to drop into any module.
 */

/** The twelve named pipeline stages. */
export const PERF_STAGE_NAMES = [
  "hash",
  "headers-parse",
  "mime-scan",
  "body-decode",
  "charset-normalise",
  "auth-results-parse",
  "address-parse",
  "threading-derive",
  "spoof-derive",
  "indicators-extract",
  "defects-collect",
  "provenance-stamp",
] as const;

export type PerfStageName = (typeof PERF_STAGE_NAMES)[number];

/** Fixture-size buckets matching the four profile inputs. */
export type PerfFixtureBucket = "small" | "medium" | "large" | "pathological";

/**
 * Per-stage, per-fixture-size wall-clock budgets in milliseconds. Each
 * number is ~2× the median measured time on a healthy M-series laptop
 * (Wave 9A iter480 baseline). The numbers carry plenty of slack so
 * shared CI runners don't flake — the goal is a 5x-regression tripwire
 * not a stopwatch.
 *
 * `resolveStageBudget` multiplies the relevant cell by
 * {@link CI_BUDGET_RELAX_MULTIPLIER} (see `./budgets`) when `CI` is set.
 */
export const PERF_BUDGETS_PER_STAGE: Record<
  PerfStageName,
  Record<PerfFixtureBucket, number>
> = {
  hash: { small: 4, medium: 8, large: 150, pathological: 16 },
  "headers-parse": { small: 2, medium: 5, large: 20, pathological: 8 },
  "mime-scan": { small: 2, medium: 5, large: 60, pathological: 30 },
  "body-decode": { small: 2, medium: 8, large: 300, pathological: 40 },
  "charset-normalise": { small: 2, medium: 4, large: 20, pathological: 6 },
  "auth-results-parse": { small: 2, medium: 4, large: 6, pathological: 4 },
  "address-parse": { small: 2, medium: 4, large: 6, pathological: 4 },
  "threading-derive": { small: 2, medium: 4, large: 6, pathological: 4 },
  "spoof-derive": { small: 2, medium: 4, large: 6, pathological: 4 },
  "indicators-extract": { small: 2, medium: 6, large: 20, pathological: 8 },
  "defects-collect": { small: 2, medium: 4, large: 8, pathological: 4 },
  "provenance-stamp": { small: 4, medium: 12, large: 200, pathological: 40 },
};

/** Total wall-clock budget per fixture bucket (sum across stages). */
export const PERF_TOTAL_BUDGET_MS: Record<PerfFixtureBucket, number> = {
  small: 5,
  medium: 50,
  large: 500,
  pathological: 1000,
};

/** Multiplier above which a stage emits a defect rather than just warning. */
export const PERF_DEFECT_MULTIPLIER = 5;

/** Multiplier above which a stage logs a parser-trace warning. */
export const PERF_WARN_MULTIPLIER = 1;

/**
 * Pluck the budget for one stage at one fixture size. Always returns a
 * finite positive number.
 */
export function resolveStageBudget(
  stage: PerfStageName,
  bucket: PerfFixtureBucket,
): number {
  return PERF_BUDGETS_PER_STAGE[stage][bucket];
}

/** A single recorded stage timing. */
export interface StageTiming {
  name: PerfStageName;
  durationMs: number;
  /** True iff the timing exceeded `PERF_WARN_MULTIPLIER * budget`. */
  warned: boolean;
  /** True iff the timing exceeded `PERF_DEFECT_MULTIPLIER * budget`. */
  overran: boolean;
}

/** Optional trace-channel sink. Production wires the parser-trace log here. */
export type PerfTraceSink = (
  level: "warn" | "error",
  message: string,
  fields: Record<string, unknown>,
) => void;

/** Optional sink that records overruns as Defects on the parser collector. */
export type PerfDefectSink = (stage: PerfStageName, durationMs: number) => void;

export interface StageMeasureOptions {
  /** Which size bucket this pipeline run falls into. Drives budget lookup. */
  bucket?: PerfFixtureBucket;
  /** Optional parser-trace sink. No-op when omitted. */
  trace?: PerfTraceSink;
  /** Optional defect-emitting sink. No-op when omitted. */
  defects?: PerfDefectSink;
}

/** Result of `.finalise()` — immutable timing rollup. */
export interface StageMeasureResult {
  bucket: PerfFixtureBucket;
  timings: readonly StageTiming[];
  totalMs: number;
  /** Stages over the warn multiplier. */
  warnings: readonly PerfStageName[];
  /** Stages over the defect multiplier. */
  overruns: readonly PerfStageName[];
}

/**
 * Per-pipeline-run helper. Allocate one at the top of `parseEmail` /
 * `analyzeEmail` and thread it through. Each `start(name)` returns a
 * `record()` closure — call it when the stage finishes.
 */
export interface StageMeasure {
  /** Start a stage. Returns a closure that records the elapsed time. */
  start(name: PerfStageName): () => void;
  /**
   * Convenience: time a synchronous function. Returns its result.
   */
  time<T>(name: PerfStageName, fn: () => T): T;
  /**
   * Manually record a duration without using a start closure — useful
   * when the work happens behind a separate timer (e.g. crypto.subtle).
   */
  record(name: PerfStageName, durationMs: number): void;
  /** Returns the rollup. Idempotent. */
  finalise(): StageMeasureResult;
}

/**
 * Pick the bucket for a given source size. Heuristic: ≤ 4 KB = small,
 * ≤ 250 KB = medium, ≤ 10 MB = large, otherwise pathological (the cap
 * isn't a hard line — extreme-parts messages are tagged pathological
 * by the caller via `forceBucket`).
 */
export function bucketForBytes(bytes: number): PerfFixtureBucket {
  if (bytes <= 4 * 1024) return "small";
  if (bytes <= 250 * 1024) return "medium";
  if (bytes <= 10 * 1024 * 1024) return "large";
  return "pathological";
}

/**
 * Create a {@link StageMeasure}. The instance is single-use — call
 * `finalise()` once at the end of the pipeline.
 */
export function createStageMeasure(options: StageMeasureOptions = {}): StageMeasure {
  const bucket = options.bucket ?? "medium";
  const trace = options.trace;
  const defects = options.defects;
  const timings: StageTiming[] = [];

  function evaluate(name: PerfStageName, durationMs: number): StageTiming {
    const budget = resolveStageBudget(name, bucket);
    const warned = durationMs > budget * PERF_WARN_MULTIPLIER;
    const overran = durationMs > budget * PERF_DEFECT_MULTIPLIER;
    if (warned) {
      trace?.(overran ? "error" : "warn", `[perf] stage ${name} over budget`, {
        stage: name,
        durationMs: Math.round(durationMs * 1000) / 1000,
        budgetMs: budget,
        bucket,
        overran,
      });
    }
    if (overran) {
      defects?.(name, durationMs);
    }
    return { name, durationMs, warned, overran };
  }

  function pushTiming(name: PerfStageName, durationMs: number): void {
    timings.push(evaluate(name, Math.max(0, durationMs)));
  }

  return {
    start(name) {
      const t0 = performance.now();
      let ended = false;
      return () => {
        if (ended) return;
        ended = true;
        pushTiming(name, performance.now() - t0);
      };
    },
    time<T>(name: PerfStageName, fn: () => T): T {
      const t0 = performance.now();
      try {
        return fn();
      } finally {
        pushTiming(name, performance.now() - t0);
      }
    },
    record(name, durationMs) {
      pushTiming(name, durationMs);
    },
    finalise() {
      const total = timings.reduce((n, t) => n + t.durationMs, 0);
      const warnings = timings.filter((t) => t.warned).map((t) => t.name);
      const overruns = timings.filter((t) => t.overran).map((t) => t.name);
      return {
        bucket,
        timings: timings.slice(),
        totalMs: total,
        warnings,
        overruns,
      };
    },
  };
}

// ---------- Rolling session sampler ----------------------------------
// DebugOverlay reads these. Pure data, no React. The parser pipeline
// pushes a rollup on each pipeline run; the overlay reads p50 / p95 /
// p99 over the last N samples (capped to avoid unbounded growth).

const SESSION_SAMPLE_CAP = 100;

interface SessionSamples {
  parse: number[];
  analyse: number[];
  perStage: Map<PerfStageName, number[]>;
}

const session: SessionSamples = {
  parse: [],
  analyse: [],
  perStage: new Map(),
};

/**
 * Record one pipeline run for the rolling session sampler. Called by
 * the parser + analyser after each `finalise()`.
 */
export function recordPerfSample(
  kind: "parse" | "analyse",
  result: StageMeasureResult,
): void {
  const arr = kind === "parse" ? session.parse : session.analyse;
  arr.push(result.totalMs);
  if (arr.length > SESSION_SAMPLE_CAP) arr.splice(0, arr.length - SESSION_SAMPLE_CAP);
  for (const t of result.timings) {
    let xs = session.perStage.get(t.name);
    if (!xs) {
      xs = [];
      session.perStage.set(t.name, xs);
    }
    xs.push(t.durationMs);
    if (xs.length > SESSION_SAMPLE_CAP) xs.splice(0, xs.length - SESSION_SAMPLE_CAP);
  }
}

/** Reset every rolling sampler. Used by tests. */
export function resetPerfSession(): void {
  session.parse.length = 0;
  session.analyse.length = 0;
  session.perStage.clear();
}

/** Quantile helper — `q` in [0, 1]. Returns 0 on empty input. */
export function quantile(values: readonly number[], q: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(q * sorted.length)));
  return sorted[idx]!;
}

export interface PerfSessionSnapshot {
  parseSamples: number;
  analyseSamples: number;
  parseP50: number;
  parseP95: number;
  parseP99: number;
  analyseP50: number;
  analyseP95: number;
  analyseP99: number;
  perStage: Array<{
    name: PerfStageName;
    samples: number;
    p50: number;
    p95: number;
    p99: number;
  }>;
}

/** Snapshot the rolling session sampler. Cheap; safe to call in render. */
export function snapshotPerfSession(): PerfSessionSnapshot {
  const perStage: PerfSessionSnapshot["perStage"] = [];
  for (const name of PERF_STAGE_NAMES) {
    const xs = session.perStage.get(name);
    if (!xs || xs.length === 0) continue;
    perStage.push({
      name,
      samples: xs.length,
      p50: quantile(xs, 0.5),
      p95: quantile(xs, 0.95),
      p99: quantile(xs, 0.99),
    });
  }
  return {
    parseSamples: session.parse.length,
    analyseSamples: session.analyse.length,
    parseP50: quantile(session.parse, 0.5),
    parseP95: quantile(session.parse, 0.95),
    parseP99: quantile(session.parse, 0.99),
    analyseP50: quantile(session.analyse, 0.5),
    analyseP95: quantile(session.analyse, 0.95),
    analyseP99: quantile(session.analyse, 0.99),
    perStage,
  };
}

// ---------- Bundle-size baseline -------------------------------------
// `scripts/check-bundle-budget.mjs` measures the actual numbers post-
// build. The DebugOverlay's Performance tab surfaces the *target*
// budget (raw + gzip) so a developer can compare at a glance. Numbers
// kept in sync with that script.

export const BUNDLE_BUDGET_RAW_KB = 1000;
export const BUNDLE_BUDGET_GZIP_KB = 300;
